import { useEffect, useRef, useState } from 'react';
import { blip } from './audio';
import type { Speaker } from './dialogue';

/**
 * Typewriter reveal, ~30 characters per second, with a pixel blip every two to
 * three characters.
 *
 * The text is always fully present in the DOM — revealing is done by clipping,
 * not by growing a string. A screen reader gets the whole line at once, and
 * copy-paste works, which it would not if characters were appended one at a
 * time.
 */

const CHARS_PER_SEC = 30;

export function Typewriter({
  text,
  speaker,
  /** Bumped by the parent to restart the reveal on the same text. */
  revealKey,
  onDone,
  skipped,
}: {
  text: string;
  speaker: Speaker;
  revealKey: number;
  onDone?: () => void;
  /** When true the line appears instantly — the tap-to-complete path. */
  skipped: boolean;
}) {
  const [shown, setShown] = useState(0);
  const doneRef = useRef(false);

  useEffect(() => {
    setShown(0);
    doneRef.current = false;
  }, [revealKey]);

  useEffect(() => {
    if (skipped) {
      setShown(text.length);
      if (!doneRef.current) {
        doneRef.current = true;
        onDone?.();
      }
      return;
    }
    if (shown >= text.length) {
      if (!doneRef.current) {
        doneRef.current = true;
        onDone?.();
      }
      return;
    }

    const id = window.setTimeout(() => {
      setShown((n) => {
        const next = n + 1;
        // Every 2–3 characters, and never on a space — a blip on whitespace
        // sounds like a stutter.
        if (text[n] !== ' ' && next % (2 + (next % 2)) === 0) blip(speaker);
        return next;
      });
    }, 1000 / CHARS_PER_SEC);

    return () => clearTimeout(id);
  }, [shown, text, skipped, speaker, onDone]);

  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  if (reduceMotion) {
    return <p className="selectable whitespace-pre-wrap">{text}</p>;
  }

  return (
    <p className="selectable whitespace-pre-wrap">
      <span>{text.slice(0, shown)}</span>
      {/* Kept in the DOM but invisible, so the box does not reflow as the
          line fills and the layout never jumps mid-sentence. */}
      <span aria-hidden className="opacity-0">
        {text.slice(shown)}
      </span>
    </p>
  );
}
